import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { IConsultOrderSave, useConsultOrderStore } from '../../../store/useConsultOrder';
import { usePageAlertStore } from '../../../store/usePageAlert';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload,
  faDownload,
  faSpinner,
  faPlus,
  faTrash,
  faArrowDown,
  faKeyboard,
  faCheckCircle,
  faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import KoreanLunarCalendar from 'korean-lunar-calendar';

// 엑셀 헤더 -> 영문 필드 매핑
const headerMapping: { [key: string]: string } = {
  '순번': 'sequence',
  '이름': 'name',
  '양/음력': 'solarType',
  '생년': 'year',
  '생월': 'month',
  '생일': 'day',
  '생시': 'hour',
  '생분': 'minute',
  '성별': 'sex',
};

const calendar = new KoreanLunarCalendar();

// 유효성 검사
const validateData = (data: IConsultOrderSave[]) => {
  const errors: { [key: number]: { [key: string]: string } } = {};

  data.forEach((row, index) => {
    const rowErrors: { [key: string]: string } = {};

    if (!row.name || row.name.trim() === '') {
      rowErrors.name = '이름은 필수입니다.';
    }

    if (!row.solarType || !['양력', '음력', '음력(윤달)'].includes(row.solarType)) {
      rowErrors.solarType = '양/음력은 양력, 음력, 음력(윤달) 중 하나여야 합니다.';
    }

    if (!row.year || isNaN(Number(row.year)) || Number(row.year) < 1900 || Number(row.year) > new Date().getFullYear()) {
      rowErrors.year = '올바른 생년을 입력해주세요.';
    }

    if (!row.month || isNaN(Number(row.month)) || Number(row.month) < 1 || Number(row.month) > 12) {
      rowErrors.month = '생월은 1~12 사이여야 합니다.';
    }

    if (!row.day || isNaN(Number(row.day)) || Number(row.day) < 1 || Number(row.day) > 31) {
      rowErrors.day = '생일은 1~31 사이여야 합니다.';
    }

    // 달력 유효성 체크 (기본 검증 통과 후)
    if (!rowErrors.year && !rowErrors.month && !rowErrors.day && row.solarType) {
      const year = Number(row.year);
      const month = Number(row.month);
      const day = Number(row.day);

      if (row.solarType === '양력') {
        const validDate = calendar.setSolarDate(year, month, day);
        if (!validDate) {
          rowErrors.day = '달력에 없는 날짜예요. 다시 확인해 주세요.';
        }
      } else if (row.solarType === '음력') {
        const lunarValid = calendar.setLunarDate(year, month, day, false);
        if (!lunarValid) {
          rowErrors.day = '달력에 없는 날짜예요. 다시 확인해 주세요.';
        }
      } else if (row.solarType === '음력(윤달)') {
        const lunarValid = calendar.setLunarDate(year, month, day, true);
        if (!lunarValid) {
          rowErrors.day = '달력에 없는 날짜예요. 다시 확인해 주세요.';
        }
      }
    }

    if (!row.sex || !['남성', '여성', 'M', 'F'].includes(row.sex)) {
      rowErrors.sex = '성별은 남성, 여성 중 하나여야 합니다.';
    }

    if (Object.keys(rowErrors).length > 0) {
      errors[index] = rowErrors;
    }
  });

  return errors;
};

const ConsultOrderRegistration = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [editingCell, setEditingCell] = useState<{
    row: number;
    field: string;
  } | null>(null);
  const [selectedCell, setSelectedCell] = useState<{
    row: number;
    field: string;
  } | null>(null);
  const [tempValue, setTempValue] = useState<string>('');
  const tableRef = useRef<HTMLTableElement>(null);

  const [files, setFiles] = useState<File[]>([]);
  // 2. 기본값으로 빈 행 1줄 세팅
  const [excelData, setExcelData] = useState<IConsultOrderSave[]>([
    {
      sequence: '1',
      name: '',
      solarType: '',
      year: '',
      month: '',
      day: '',
      hour: '',
      minute: '',
      sex: '',
    },
  ]);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // 최신 excelData를 참조하기 위한 ref
  const excelDataRef = useRef<IConsultOrderSave[]>(excelData);
  useEffect(() => {
    excelDataRef.current = excelData;
  }, [excelData]);

  const { postOrder, postOrderLoading } = useConsultOrderStore();
  const { setAlert, clearAlert, setButton, clearAll, setButtonLoading } = usePageAlertStore();

  // 테이블 컬럼 정의
  const columns = useMemo(
    () => [
      { field: 'sequence', header: '순번', width: '60px' },
      { field: 'name', header: '이름', width: '100px' },
      { field: 'solarType', header: '양/음력', width: '80px' },
      { field: 'year', header: '생년', width: '70px' },
      { field: 'month', header: '생월', width: '60px' },
      { field: 'day', header: '생일', width: '60px' },
      { field: 'hour', header: '생시', width: '60px' },
      { field: 'minute', header: '생분', width: '60px' },
      { field: 'sex', header: '성별', width: '60px' },
    ],
    []
  );

  useEffect(() => {
    if (excelData.length > 0) {
      const validation = validateData(excelData);
      setValidationResult(validation);
    }
  }, [excelData]);

  // validationResult가 변경될 때 Title에 알림 및 버튼 설정
  useEffect(() => {
    if (!validationResult || excelData.length === 0) {
      clearAll();
      return;
    }

    const errorCount = Object.keys(validationResult).length;

    if (errorCount === 0) {
      setAlert('success', '입력 내용에 이상이 없습니다', `총 ${excelData.length}건`);
      // 등록 버튼 설정
      setButton('전체 일괄 등록', () => {
        // handleBulkSubmit 호출을 위해 이벤트 발생
        const event = new CustomEvent('consultOrderBulkSubmit');
        window.dispatchEvent(event);
      });
    } else {
      const firstErrorRow = Object.keys(validationResult).map(Number).sort((a, b) => a - b)[0];
      const firstErrors = validationResult[firstErrorRow];
      const firstErrorField = Object.keys(firstErrors)[0];
      const firstErrorMessage = firstErrors[firstErrorField];
      const restCount = errorCount - 1;

      setAlert(
        'error',
        `${firstErrorRow + 1}행: ${firstErrorMessage}`,
        restCount > 0 ? `외 ${restCount}건` : undefined,
        () => {
          setSelectedCell({ row: firstErrorRow, field: firstErrorField });
          setTimeout(() => {
            const cellId = `cell-${firstErrorRow}-${firstErrorField}`;
            const cellElement = document.getElementById(cellId);
            if (cellElement) {
              cellElement.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
            }
          }, 50);
        }
      );
    }
  }, [validationResult, excelData.length, setAlert, setButton, clearAll]);

  // 커스텀 이벤트 리스너 - 헤더 버튼 클릭시 일괄 등록 호출
  useEffect(() => {
    const handleSubmitEvent = () => {
      if (editingCell) {
        toast.warn('수정을 완료하고 등록해주세요.');
        return;
      }
      postOrder(excelDataRef.current, navigate);
    };
    window.addEventListener('consultOrderBulkSubmit', handleSubmitEvent);
    return () => {
      window.removeEventListener('consultOrderBulkSubmit', handleSubmitEvent);
    };
  }, [editingCell, navigate, postOrder]);

  // 로딩 상태 동기화
  useEffect(() => {
    setButtonLoading(postOrderLoading);
  }, [postOrderLoading, setButtonLoading]);

  // 페이지 이탈 시 알림 지우기
  useEffect(() => {
    return () => {
      clearAll();
    };
  }, [clearAll]);

  // 파일 처리 공통 함수
  const processFile = useCallback((file: File) => {
    if (!file.name.endsWith('.xlsx')) {
      toast.error('엑셀 파일(.xlsx)만 업로드 가능합니다.');
      return;
    }

    setIsUploading(true);
    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      const arrayBuffer = e.target?.result;
      if (arrayBuffer) {
        const data = new Uint8Array(arrayBuffer as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const headers = XLSX.utils.sheet_to_json<string[]>(worksheet, {
          header: 1,
        })[1];

        if (Array.isArray(headers)) {
          const jsonData: { [key: string]: any }[] = XLSX.utils.sheet_to_json(
            worksheet,
            { header: headers, range: 2 }
          );

          // 헤더 매핑
          const transformedData = jsonData.map((row) => {
            const newRow: any = {};
            Object.keys(row).forEach(key => {
              const mappedKey = headerMapping[key] || key;
              newRow[mappedKey] = row[key];
            });
            return newRow as IConsultOrderSave;
          });

          // 3. 엑셀 업로드시 기존 데이터를 싹 날리고 엑셀 데이터로 교체
          const reorderedData = transformedData.map((row, idx) => ({
            ...row,
            sequence: String(idx + 1),
          }));
          setExcelData(reorderedData);
          setFiles([file]);
          setIsUploading(false);
        }
      }
    };

    reader.readAsArrayBuffer(file);
  }, []);

  // 파일 업로드 처리
  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      processFile(file);
    },
    [processFile]
  );

  // 드래그 앤 드롭 처리
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        processFile(files[0]);
      }
    },
    [processFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }
    setIsDragging(false);
  }, []);

  // 셀 클릭 (선택)
  const handleCellClick = useCallback((rowIndex: number, field: string) => {
    setSelectedCell({ row: rowIndex, field });
    if (tableRef.current) {
      tableRef.current.focus();
    }
  }, []);

  // 셀 편집 시작 (더블클릭)
  const handleCellDoubleClick = useCallback(
    (rowIndex: number, field: string) => {
      setEditingCell({ row: rowIndex, field });
      setTempValue(String((excelData[rowIndex] as any)[field] || ''));
    },
    [excelData]
  );

  // 셀 편집 완료
  const handleCellChange = useCallback(
    (rowIndex: number, field: string, value: string) => {
      const updatedData = [...excelData];
      (updatedData[rowIndex] as any)[field] = value;
      setExcelData(updatedData);
      setEditingCell(null);

      setTimeout(() => {
        if (tableRef.current) {
          tableRef.current.focus();
        }
      }, 0);
    },
    [excelData]
  );

  // 선택 토글
  const toggleRowSelection = useCallback(
    (rowIndex: number) => {
      const newSelected = new Set(selectedRows);
      if (newSelected.has(rowIndex)) {
        newSelected.delete(rowIndex);
      } else {
        newSelected.add(rowIndex);
      }
      setSelectedRows(newSelected);
    },
    [selectedRows]
  );

  // 전체 선택/해제
  const toggleAllSelection = useCallback(() => {
    if (selectedRows.size === excelData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(excelData.map((_, i) => i)));
    }
  }, [selectedRows, excelData]);

  // 키보드로 셀 이동 및 삭제
  const handleKeyNavigation = useCallback(
    (e: React.KeyboardEvent) => {
      if (!selectedCell) return;
      if (editingCell) return;

      const currentColIndex = columns.findIndex(
        col => col.field === selectedCell.field
      );
      let newRow = selectedCell.row;
      let newColIndex = currentColIndex;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          if (selectedCell.row > 0) newRow = selectedCell.row - 1;
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (selectedCell.row < excelData.length - 1)
            newRow = selectedCell.row + 1;
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (currentColIndex > 0) {
            newColIndex = currentColIndex - 1;
          } else if (selectedCell.row > 0) {
            newRow = selectedCell.row - 1;
            newColIndex = columns.length - 1;
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (currentColIndex < columns.length - 1) {
            newColIndex = currentColIndex + 1;
          } else if (selectedCell.row < excelData.length - 1) {
            newRow = selectedCell.row + 1;
            newColIndex = 0;
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (!e.shiftKey) {
            setEditingCell({
              row: selectedCell.row,
              field: selectedCell.field,
            });
            const currentValue =
              (excelData[selectedCell.row] as any)[selectedCell.field] || '';
            setTempValue(String(currentValue));
          }
          return;
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          handleCellChange(selectedCell.row, selectedCell.field, '');
          return;
        case 'Tab':
          e.preventDefault();
          if (!e.shiftKey) {
            if (currentColIndex < columns.length - 1) {
              newColIndex = currentColIndex + 1;
            } else if (selectedCell.row < excelData.length - 1) {
              newRow = selectedCell.row + 1;
              newColIndex = 0;
            }
          } else {
            if (currentColIndex > 0) {
              newColIndex = currentColIndex - 1;
            } else if (selectedCell.row > 0) {
              newRow = selectedCell.row - 1;
              newColIndex = columns.length - 1;
            }
          }
          break;
        case ' ':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            toggleRowSelection(selectedCell.row);
            return;
          }
          break;
        default:
          return;
      }

      const nextColumn = columns[newColIndex];
      if (nextColumn) {
        setSelectedCell({ row: newRow, field: nextColumn.field });
      }

      setTimeout(() => {
        const nextColumn = columns[newColIndex];
        if (!nextColumn) return;
        const cellId = `cell-${newRow}-${nextColumn.field}`;
        const cellElement = document.getElementById(cellId);
        if (cellElement) {
          cellElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        }
      }, 0);
    },
    [
      selectedCell,
      editingCell,
      columns,
      excelData,
      handleCellChange,
      toggleRowSelection,
    ]
  );

  // 행 추가 (5. 양력 기본값 제거 - 공란으로)
  const handleAddRow = useCallback(() => {
    const newRow: IConsultOrderSave = {
      sequence: String(excelData.length + 1),
      name: '',
      solarType: '',
      year: '',
      month: '',
      day: '',
      hour: '',
      minute: '',
      sex: '',
    };
    setExcelData([...excelData, newRow]);
    // 새 행의 첫 번째 셀 선택
    setTimeout(() => {
      setSelectedCell({ row: excelData.length, field: 'name' });
      if (tableRef.current) {
        tableRef.current.focus();
      }
    }, 0);
  }, [excelData]);

  // 선택된 행 삭제
  const handleDeleteSelectedRows = useCallback(() => {
    if (selectedRows.size === 0) {
      toast.warn('삭제할 행을 선택해주세요.');
      return;
    }
    const newData = excelData.filter((_, index) => !selectedRows.has(index));
    // 순번 재정렬
    const reorderedData = newData.map((row, idx) => ({
      ...row,
      sequence: String(idx + 1),
    }));
    setExcelData(reorderedData);
    setSelectedRows(new Set());
    setSelectedCell(null);
  }, [excelData, selectedRows]);

  // 일괄 등록
  const handleBulkSubmit = useCallback(async () => {
    if (editingCell) {
      toast.warn('수정을 완료하고 등록해주세요.');
      return;
    }

    postOrder(excelData, navigate);
  }, [editingCell, excelData, navigate, postOrder]);

  return (
    <>
      <style>{`
        .order-table-sticky-header thead,
        .order-table-sticky-header thead * {
          overflow: visible !important;
          background-color: #e5e7eb !important;
          opacity: 1 !important;
        }
      `}</style>
      <div className="!w-full !h-full !flex !flex-col !bg-white !overflow-auto">

        {/* 숨겨진 파일 입력 */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          onChange={handleFileUpload}
          className="hidden"
        />

        {/* 메인 테이블 영역 */}
        <div
          className="h-full w-full overflow-auto"
          style={{ paddingTop: '0px', paddingBottom: '60px' }}
        >
          <div
            className="bg-white"
          >
            {/* 수동 입력 안내 - 엑셀 업로드 전에만 표시 */}
            {files.length === 0 && (
              <div className="bg-gradient-to-r from-blue-50 px-4 py-3 border-b border-blue-200 flex items-center gap-3">
                <FontAwesomeIcon icon={faArrowDown} className="text-blue-400 text-xl animate-bounce" />
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faKeyboard} className="text-blue-500" />
                  <span className="text-m text-blue-700 font-semibold">수동 입력</span>
                </div>
                <span className="text-m text-blue-600">2명 이상 등록 시 아래 행 추가 버튼을 눌러 직접 고객 정보를 입력하세요.</span>
              </div>
            )}

            {/* 행 추가/삭제 버튼 + 파일 정보 */}
            <div className="sticky top-0 z-30 bg-gray-100 border-b border-gray-300 px-4 py-2 flex items-center gap-2 flex-wrap">
              {files.length === 0 && (
                <button
                  onClick={handleAddRow}
                  className="inline-flex items-center px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm font-medium transition-colors"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1.5" />
                  행 추가
                </button>
              )}
              {selectedRows.size > 0 && (
                <button
                  onClick={handleDeleteSelectedRows}
                  className="inline-flex items-center px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded text-sm font-medium transition-colors"
                >
                  <FontAwesomeIcon icon={faTrash} className="mr-1.5" />
                  선택 삭제 ({selectedRows.size}건)
                </button>
              )}

              {/* 업로드 중 표시 */}
              {isUploading && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded border border-gray-200">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-500" />
                  <span className="text-sm text-blue-600">업로드 중...</span>
                </div>
              )}

              {/* 파일 정보 표시 */}
              {files.length > 0 && (
                <div className="flex items-center text-sm text-gray-600 ml-2">
                  <span className="font-medium">파일:</span>
                  <span className="ml-2">{files[0].name}</span>
                  <span className="ml-4 text-gray-400">|</span>
                  <span className="ml-4">
                    총 <span className="font-medium text-blue-600">{excelData.length}</span>건
                  </span>
                  {validationResult && Object.keys(validationResult).length > 0 && (
                    <>
                      <span className="ml-4 text-gray-400">|</span>
                      <span className="ml-4 text-red-600">
                        오류 <span className="font-medium">{Object.keys(validationResult).length}</span>건
                      </span>
                    </>
                  )}
                </div>
              )}

              {files.length === 0 && excelData.length > 0 && (
                <span className="ml-auto text-sm text-gray-600">
                  총 <span className="font-medium text-blue-600">{excelData.length}</span>건
                </span>
              )}
            </div>

            {/* 테이블 - 데이터 있을 때만 표시 */}
            {excelData.length > 0 && (
              <table
                ref={tableRef}
                className="text-xs select-none order-table-sticky-header"
                style={{
                  minWidth: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  overflow: 'visible',
                }}
                tabIndex={0}
                onKeyDown={handleKeyNavigation}
              >
                <thead
                  className="sticky top-0 z-20"
                  style={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: '#e5e7eb',
                    overflow: 'visible',
                  }}
                >
                  <tr style={{ backgroundColor: '#e5e7eb' }}>
                    <th
                      className="border-t border-l border-r border-b-2 border-gray-400 bg-gray-200 p-0 w-12"
                      style={{
                        borderBottomColor: '#666',
                        backgroundColor: '#e5e7eb',
                        overflow: 'visible',
                      }}
                    >
                      <label
                        className="flex items-center justify-center h-full w-full p-2 cursor-pointer hover:bg-gray-300 transition-colors"
                        style={{ backgroundColor: '#e5e7eb' }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            selectedRows.size === excelData.length &&
                            excelData.length > 0
                          }
                          onChange={toggleAllSelection}
                          className="w-4 h-4 cursor-pointer text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                      </label>
                    </th>
                    {columns.map((col, index) => (
                      <th
                        key={col.field}
                        className="border-t border-r border-b-2 border-gray-400 bg-gray-200 px-2 py-1 font-medium text-left whitespace-nowrap select-none"
                        style={{
                          width: col.width,
                          minWidth: col.width,
                          borderBottomColor: '#666',
                          borderLeftWidth: index === 0 ? '1px' : '0',
                          backgroundColor: '#e5e7eb',
                          overflow: 'visible',
                        }}
                      >
                        {col.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody style={{ overflow: 'visible' }}>
                  {excelData.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className={`${
                        selectedRows.has(rowIndex)
                          ? 'bg-blue-100'
                          : 'even:bg-gray-50 odd:bg-white hover:bg-yellow-50'
                      } transition-colors`}
                    >
                      <td className="border-l border-r border-b border-gray-300 p-0 text-center bg-gray-50">
                        <label className="flex items-center justify-center h-full w-full p-2 cursor-pointer hover:bg-gray-200 transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedRows.has(rowIndex)}
                            onChange={() => toggleRowSelection(rowIndex)}
                            className="w-4 h-4 cursor-pointer text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                        </label>
                      </td>
                      {columns.map(col => {
                        const isEditing =
                          editingCell?.row === rowIndex &&
                          editingCell?.field === col.field;
                        const isSelected =
                          selectedCell?.row === rowIndex &&
                          selectedCell?.field === col.field;
                        const hasError =
                          validationResult?.[rowIndex]?.[col.field];
                        const cellValue = (row as any)[col.field] ?? '';

                        return (
                          <td
                            key={col.field}
                            id={`cell-${rowIndex}-${col.field}`}
                            className={`border-r border-b border-gray-300 px-2 py-1 cursor-cell relative group align-middle ${
                              hasError
                                ? 'bg-red-100 text-red-700 font-medium'
                                : ''
                            } ${
                              isEditing
                                ? 'ring-2 ring-blue-500 ring-inset z-10'
                                : ''
                            } ${
                              isSelected && !isEditing
                                ? 'ring-2 ring-blue-400 ring-inset bg-blue-50'
                                : ''
                            }`}
                            onClick={() => handleCellClick(rowIndex, col.field)}
                            onDoubleClick={() =>
                              !isEditing &&
                              handleCellDoubleClick(rowIndex, col.field)
                            }
                            title={hasError || ''}
                            style={{
                              minHeight: '28px',
                              borderLeftWidth:
                                columns.indexOf(col) === 0 ? '1px' : '0',
                            }}
                          >
                            {isEditing ? (
                              <input
                                type="text"
                                value={tempValue}
                                onChange={e => setTempValue(e.target.value)}
                                onBlur={() =>
                                  handleCellChange(
                                    rowIndex,
                                    col.field,
                                    tempValue
                                  )
                                }
                                onKeyDown={e => {
                                  e.stopPropagation();
                                  // IME 조합 중이면 무시 (한글 입력 시 마지막 글자 잘림 방지)
                                  if (e.nativeEvent.isComposing) return;
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleCellChange(rowIndex, col.field, tempValue);
                                    // 1. 엔터 후 다음 칸으로 이동
                                    const currentColIndex = columns.findIndex(c => c.field === col.field);
                                    if (currentColIndex < columns.length - 1) {
                                      // 같은 행의 다음 칸
                                      const nextCol = columns[currentColIndex + 1];
                                      setSelectedCell({ row: rowIndex, field: nextCol.field });
                                      setEditingCell({ row: rowIndex, field: nextCol.field });
                                      setTempValue(String((excelData[rowIndex] as any)[nextCol.field] || ''));
                                    } else if (rowIndex < excelData.length - 1) {
                                      // 다음 행의 첫 칸
                                      const nextCol = columns[0];
                                      setSelectedCell({ row: rowIndex + 1, field: nextCol.field });
                                      setEditingCell({ row: rowIndex + 1, field: nextCol.field });
                                      setTempValue(String((excelData[rowIndex + 1] as any)[nextCol.field] || ''));
                                    }
                                  } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    setEditingCell(null);
                                    setSelectedCell({
                                      row: rowIndex,
                                      field: col.field,
                                    });
                                  }
                                }}
                                className="w-full h-full px-1 py-0.5 -mx-1 border-none outline-none bg-white text-xs"
                                autoFocus
                              />
                            ) : (
                              <div className="truncate relative flex items-center h-full">
                                <span className="relative z-10">
                                  {cellValue}
                                </span>
                                {hasError && (
                                  <div className="absolute -top-8 left-0 bg-red-600 text-white text-xs px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-30">
                                    {hasError}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* 안내 화면 - 엑셀 업로드 전에만 표시 */}
            {files.length === 0 && (
            <div className="bg-white flex flex-col md:flex-row items-start md:items-stretch justify-start md:justify-center py-6 md:py-0 md:pt-8 px-4 md:px-0">
              {/* Step 1 - 설명 + 샘플 양식 큰 버튼 */}
              <div className="w-full md:w-1/2 md:border-r border-b md:border-b-0 border-dashed border-gray-300 flex items-start justify-center p-6 md:p-16">
                <div className="max-w-2xl w-full space-y-4 md:space-y-8">
                  <div className="space-y-3 md:space-y-4">
                    <div className="flex items-center">
                      <span className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full bg-blue-50 text-blue-700 font-bold ring-1 ring-blue-200 mr-3 flex-shrink-0">
                        1
                      </span>
                      <div className="text-lg md:text-3xl font-extrabold text-gray-900 tracking-tight leading-tight">
                        주문서 양식을 다운로드 받으세요
                      </div>
                    </div>
                    <div
                      className="text-sm md:text-xl text-gray-800 leading-6 md:leading-8 space-y-2"
                      style={{ textWrap: 'pretty' }}
                    >
                      <p className="font-medium">
                        상담 주문서 양식 다운로드 버튼을 클릭하여 양식을
                        다운로드&nbsp;받아&nbsp;주세요.
                      </p>
                      <p>
                        1명당 한 줄씩 고객 정보를 정확히 입력해&nbsp;주세요.
                      </p>
                      <p>
                        정확히 입력하셨다면 우측의 주문서 업로드를
                        진행해&nbsp;주세요.
                      </p>
                    </div>
                  </div>
                  <a
                    href="/상담주문등록_양식.xlsx"
                    download
                    className="w-full flex items-center justify-center px-6 py-4 md:px-10 md:py-8 text-base md:text-2xl bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl md:rounded-2xl shadow-md border border-transparent transition-colors"
                  >
                    <FontAwesomeIcon
                      icon={faDownload}
                      className="mr-3 md:mr-4 text-white"
                    />
                    <span className="font-bold">상담 주문서 양식 다운로드</span>
                  </a>
                </div>
              </div>

              {/* Step 2 - 큰 드래그앤드롭 박스 + 큰 업로드 버튼 */}
              <div className="w-full md:w-1/2 p-6 md:p-16 flex items-start justify-center">
                <div className="max-w-2xl w-full">
                  <div className="flex items-center mb-4 md:mb-5">
                    <span className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full bg-blue-50 text-blue-700 font-bold ring-1 ring-blue-200 mr-3 flex-shrink-0">
                      2
                    </span>
                    <div className="text-lg md:text-3xl font-extrabold text-gray-900 tracking-tight leading-tight">
                      주문서를 업로드 해주세요
                    </div>
                  </div>
                  <div
                    className={`w-full min-h-[250px] md:min-h-[400px] rounded-xl md:rounded-2xl border-2 border-dashed transition-all flex flex-col items-center justify-center text-center px-6 py-6 md:px-10 md:py-10 ${
                      isDragging
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-gray-300 bg-gray-50'
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                  >
                    <FontAwesomeIcon
                      icon={faUpload}
                      className={`mb-3 md:mb-6 transition-colors text-4xl md:text-7xl ${
                        isDragging ? 'text-blue-400' : 'text-gray-300'
                      }`}
                    />
                    <div
                      className={`text-base md:text-2xl font-semibold mb-2 md:mb-3 ${
                        isDragging ? 'text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      주문서를 여기에 드래그하세요
                    </div>
                    <p className="text-xs md:text-base text-gray-500 mb-4 md:mb-7">
                      또는 아래 버튼을 눌러 파일을 선택하세요
                    </p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="pointer-events-auto inline-flex items-center justify-center px-6 py-3 md:px-12 md:py-6 text-base md:text-2xl bg-blue-600 hover:bg-blue-700 text-white rounded-xl md:rounded-2xl shadow-md transition-colors"
                    >
                      <FontAwesomeIcon icon={faUpload} className="mr-2 md:mr-4" />
                      주문서 업로드
                    </button>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ConsultOrderRegistration;
